import type { Except } from 'type-fest';

import type { BlockResponseDetails } from './blockTypes';
import type { ReactionTypes } from './reactions';
import type { RepliesResponse } from './repliesTypes';

export enum BlockTypes {
  Dropdown = 'DROPDOWN',
  FileUpload = 'FILE_UPLOAD',
  OpenEnded = 'OPEN_ENDED',
  Scale = 'SCALE',
  MultiChoice = 'MULTI_CHOICE',
  Gif = 'GIF',
  PersonSelector = 'PERSON_SELECTOR',
  GivePointsStack = 'GIVE_POINTS_STACK',
}

export enum MemberState {
  New = 'NEW',
  Active = 'ACTIVE',
  Deactivated = 'DEACTIVATED',
  Pending = 'PENDING',
  Dummy = 'DUMMY',
}

export enum MemberRole {
  Admin = 'Admin',
  Employee = 'Employee',
  Owner = 'Owner',
  SuperAdmin = 'SuperAdmin',
}

export enum BlockResponseState {
  NotResponded = 'NOT_RESPONDED',
  Responded = 'RESPONDED',
  Skipped = 'SKIPPED',
}

export enum VisibilityTypes {
  Everyone = 'everyone',
  Custom = 'custom',
  OnlyOwners = 'onlyOwners',
  OnlyParticipants = 'onlyParticipants',
  Private = 'private',
}

export type MemberDetails = {
  name: string;
  firstName: string;
  lastName: string;
  username: string;
  email?: string;
  memberID: string;
  image?: string;
  pointsGiven: number;
  totalPointsGiven: number;
  memberState: MemberState;
  role: MemberRole[];
  pronouns?: string;
};

export type ReactionDetails = {
  name: string;
  value: string;
  type: ReactionTypes;
  displayName: string;
  members: MemberDetails[];
};

export type FlowData = {
  name: string;
  flowId: string;
  version?: number;
  kind: string;
  icon: {
    kind: string;
    value: string;
  };
};

export type AnnouncementFeedDetails = {
  title: string;
  color: string;
  viewsCount: number;
  description: string;
  showInsights: boolean;
  announcementId: string;
  notificationSettings: {
    email: boolean;
    push: boolean;
  };
};

export type BaseFlowPostResponse = {
  responseId: string;
  instanceId: string;
  responses: BlockResponseDetails[];
  flow: FlowData;
  reactions: ReactionDetails[];
  visibility: VisibilityTypes;
  isPrivate: boolean;
  commentsCount: number;
  createdAt: string;
  canDelete: boolean;
  canEdit: boolean;
  kind: string;
  isMuted?: boolean;
  hideReactions?: boolean;
  hideReplies?: boolean;
  replySummary?: RepliesResponse;
  showTurnOffSettings?: boolean;
  activeAnnouncement?: AnnouncementFeedDetails;
};

type NonAnonymousFlowPostResponse = BaseFlowPostResponse & {
  respondent: MemberDetails;
};

type AnonymousFlowPostResponse = Except<BaseFlowPostResponse, 'instanceId'> & {
  respondent: null;
};

export type FlowPostResponse =
  | NonAnonymousFlowPostResponse
  | AnonymousFlowPostResponse;

export type FileDownloadRequest = {
  flowId: string;
  blockId: string;
  fileName: string;
  instanceId: string;
  responseId: string;
};

export type DownloadSecureFileResponse = {
  url: string;
  expiresOn: Date;
};

export type FlowStatusToDisplay = 'archive' | 'unarchive';
